import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { CSSProperties } from 'react'
import { ArrowLeft, ArrowRight, Pause, Play, Repeat2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import legacyShortVideoContract from '@legacy-short-videos-contract'
import {
  disableYoutubeCaptionsCommands,
  getYoutubeEmbedUrl,
} from '@shared/media/youtube/youtube-embed-url'
import { MediaControlBar } from '@shared/media/media-control-bar'
import {
  LmsButton,
  SafeHtml,
  SelectMenu,
  SelectMenuContent,
  SelectMenuItem,
  SelectMenuTrigger,
  SelectMenuValue,
} from '@shared/ui'
import {
  getShortVideoThumbnail,
  getShortVideoTranslationText,
} from '@features/short-videos/shared/short-video-helpers'
import type { ShortVideo } from '@services/firebase/short-videos'

type TranscriptMode = 'all' | 'target' | 'vi'

const videoSpeeds = [
  { label: 'Very slow', value: 0.5 },
  { label: 'Slow', value: 0.75 },
  { label: 'Normal', value: 1 },
  { label: 'Fast', value: 1.25 },
  { label: 'Very fast', value: 1.5 },
] as const

const PLAYER_BOOT_DELAY_MS = 160
const PLAYER_PROGRESS_TICK_MS = 100

const YOUTUBE_PLAYER_STATE = {
  ENDED: 0,
  PAUSED: 2,
  PLAYING: 1,
} as const

type PlaybackStateOptions = {
  readonly updateIntent?: boolean
  readonly sendCommand?: boolean
}

type YoutubePlaybackMessage = {
  readonly currentTime?: number
  readonly playerState?: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function getYoutubePlaybackMessage(data: unknown): YoutubePlaybackMessage | null {
  if (typeof data !== 'string') {
    return null
  }

  let payload: unknown

  try {
    payload = JSON.parse(data)
  } catch {
    return null
  }

  if (!isRecord(payload)) {
    return null
  }

  if (payload.event === 'onStateChange' && typeof payload.info === 'number') {
    return { playerState: payload.info }
  }

  if (payload.event !== 'infoDelivery' || !isRecord(payload.info)) {
    return null
  }

  const currentTime = payload.info.currentTime
  const playerState = payload.info.playerState

  return {
    ...(typeof currentTime === 'number' && Number.isFinite(currentTime) ? { currentTime } : {}),
    ...(typeof playerState === 'number' ? { playerState } : {}),
  }
}

function getVideoSegments(video: ShortVideo) {
  if (video.content.length > 0) {
    return video.content
  }

  return [
    {
      end: 60,
      start: 0,
      text: video.title,
      translates: video.translates,
    },
  ]
}

function getSegmentStart(
  segment: ShortVideo['content'][number] | undefined,
  fallback: number,
) {
  return typeof segment?.start === 'number' ? segment.start : fallback
}

function getTranscriptModeLabel({
  locale,
  mode,
}: {
  readonly locale: string
  readonly mode: TranscriptMode
}) {
  if (mode === 'all') {
    return 'All'
  }

  if (mode === 'vi') {
    return 'vi'
  }

  return locale
}

export type ShortVideoLearningPlayerHandle = {
  readonly pause: () => void
}

export const ShortVideoLearningPlayer = forwardRef<
  ShortVideoLearningPlayerHandle,
  {
    readonly locale: string
    readonly onReturn: () => void
    readonly video: ShortVideo
  }
>(function ShortVideoLearningPlayer({
  locale,
  onReturn,
  video,
}, ref) {
  const { t } = useTranslation('translation')
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const autoplayTimeoutRef = useRef<number | null>(null)
  const progressTimerRef = useRef<number | null>(null)
  const playbackIntentRef = useRef(false)
  const isPlayingRef = useRef(false)
  const slideIndexRef = useRef(0)
  const loopSlideRef = useRef(false)
  const loopNumberLoopedRef = useRef(legacyShortVideoContract.LEGACY_MAIN_SENTENCE_PASS_COUNT)
  const playbackRateRef = useRef(1)
  const segments = useMemo(() => getVideoSegments(video), [video])
  const firstSegment = segments[0]
  const { end: videoEnd, start: videoStart } = useMemo(
    () => legacyShortVideoContract.getLegacyShortVideoVideoBounds(segments),
    [segments],
  )
  const thumbnail = useMemo(
    () => getShortVideoThumbnail(video),
    [video],
  )
  const [slideIndex, setSlideIndex] = useState(0)
  const [isPlayerLoaded, setIsPlayerLoaded] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [playedSeconds, setPlayedSeconds] = useState(videoStart)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [loopSlide, setLoopSlide] = useState(false)
  const [loopNumberLooped, setLoopNumberLooped] = useState(
    legacyShortVideoContract.LEGACY_MAIN_SENTENCE_PASS_COUNT,
  )
  const [transcriptMode, setTranscriptMode] = useState<TranscriptMode>('all')
  const currentSegment = segments[Math.min(slideIndex, segments.length - 1)] ?? firstSegment
  const embedUrl = useMemo(
    () => video.youtubeId
      ? getYoutubeEmbedUrl(video.youtubeId, {
          additionalParams: {
            disablekb: '0',
            mute: '0',
            wmode: 'transparent',
          },
          autoplay: false,
          controls: false,
          enableJsApi: true,
          fullscreen: false,
          start: videoStart,
        })
      : null,
    [video.youtubeId, videoStart],
  )
  const segmentText = currentSegment?.text ?? video.title ?? ''
  const segmentTranslation = getShortVideoTranslationText(currentSegment?.translates, locale)
  const progress =
    videoEnd > videoStart
      ? Math.min(100, Math.max(0, ((playedSeconds - videoStart) / (videoEnd - videoStart)) * 100))
      : 0

  const sendPlayerCommand = useCallback((func: string, args: unknown[] = []) => {
    iframeRef.current?.contentWindow?.postMessage(
      JSON.stringify({ args, event: 'command', func }),
      '*',
    )
  }, [])

  const disableYoutubeCaptions = useCallback(() => {
    for (const command of disableYoutubeCaptionsCommands) {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify(command), '*')
    }
  }, [])

  const clearAutoplayTimeout = useCallback(() => {
    if (autoplayTimeoutRef.current !== null) {
      window.clearTimeout(autoplayTimeoutRef.current)
      autoplayTimeoutRef.current = null
    }
  }, [])

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current !== null) {
      window.clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }
  }, [])

  const setPlaybackState = useCallback(
    (
      nextPlaying: boolean,
      { sendCommand = true, updateIntent = true }: PlaybackStateOptions = {},
    ) => {
      if (updateIntent) {
        playbackIntentRef.current = nextPlaying
      }

      isPlayingRef.current = nextPlaying

      if (!nextPlaying) {
        clearProgressTimer()
        if (updateIntent) {
          clearAutoplayTimeout()
        }
      }

      setIsPlaying(nextPlaying)

      if (sendCommand && embedUrl) {
        sendPlayerCommand(nextPlaying ? 'playVideo' : 'pauseVideo')
      }
    },
    [clearAutoplayTimeout, clearProgressTimer, embedUrl, sendPlayerCommand],
  )

  useImperativeHandle(
    ref,
    () => ({
      pause: () => {
        setPlaybackState(false)
      },
    }),
    [setPlaybackState],
  )

  useEffect(() => {
    slideIndexRef.current = slideIndex
  }, [slideIndex])

  useEffect(() => {
    loopSlideRef.current = loopSlide
  }, [loopSlide])

  useEffect(() => {
    loopNumberLoopedRef.current = loopNumberLooped
  }, [loopNumberLooped])

  useEffect(() => {
    playbackRateRef.current = playbackRate
  }, [playbackRate])

  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [isPlaying])

  const handleTogglePlay = useCallback(() => {
    if (!embedUrl) {
      return
    }

    setPlaybackState(!isPlayingRef.current)
  }, [embedUrl, setPlaybackState])

  const handleSeekPercent = useCallback(
    (value: number) => {
      const nextSeconds =
        videoStart + ((videoEnd - videoStart) * Math.min(100, Math.max(0, value))) / 100

      setPlayedSeconds(nextSeconds)
      sendPlayerCommand('seekTo', [nextSeconds, true])
    },
    [sendPlayerCommand, videoEnd, videoStart],
  )

  const handleNavigateToSlide = (requestedSlideIndex: number) => {
    const nextIndex = legacyShortVideoContract.getLegacyShortVideoNavigationTransition({
      loopNumberLooped,
      slideIndex: requestedSlideIndex,
    }).slideIndex
    const nextSegment = segments[nextIndex] ?? firstSegment
    const nextStart = getSegmentStart(nextSegment, videoStart)

    slideIndexRef.current = nextIndex
    setSlideIndex(nextIndex)
    setPlayedSeconds(nextStart)
    sendPlayerCommand('seekTo', [nextStart, true])
    setPlaybackState(true)
  }

  const handleCycleTranscript = () => {
    setTranscriptMode((currentMode) => {
      if (currentMode === 'all') {
        return 'vi'
      }

      if (currentMode === 'vi' && locale !== 'vi') {
        return 'target'
      }

      return 'all'
    })
  }

  const handlePlayerLoad = () => {
    setIsPlayerLoaded(true)
    setSlideIndex(0)
    setPlayedSeconds(videoStart)
    setLoopNumberLooped(legacyShortVideoContract.LEGACY_MAIN_SENTENCE_PASS_COUNT)
    setPlaybackState(false, { sendCommand: false })
    disableYoutubeCaptions()
    sendPlayerCommand('addEventListener', ['onStateChange'])
    sendPlayerCommand('seekTo', [videoStart, true])
    sendPlayerCommand('setPlaybackRate', [playbackRate])

    if (!embedUrl) {
      return
    }

    clearAutoplayTimeout()
    autoplayTimeoutRef.current = window.setTimeout(() => {
      setPlaybackState(true, { sendCommand: false })
      sendPlayerCommand('seekTo', [videoStart, true])
      sendPlayerCommand('playVideo')
      autoplayTimeoutRef.current = null
    }, PLAYER_BOOT_DELAY_MS)
  }

  useEffect(() => {
    sendPlayerCommand('setPlaybackRate', [playbackRate])
  }, [playbackRate, sendPlayerCommand])

  useEffect(() => {
    const handleYoutubeMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return
      }

      const message = getYoutubePlaybackMessage(event.data)

      if (!message) {
        return
      }

      if (
        typeof message.currentTime === 'number' &&
        (isPlayingRef.current ||
          message.playerState === YOUTUBE_PLAYER_STATE.PAUSED ||
          message.playerState === YOUTUBE_PLAYER_STATE.PLAYING ||
          message.playerState === YOUTUBE_PLAYER_STATE.ENDED)
      ) {
        setPlayedSeconds(Math.min(videoEnd, Math.max(videoStart, message.currentTime)))
      }

      if (message.playerState === YOUTUBE_PLAYER_STATE.PLAYING) {
        if (playbackIntentRef.current) {
          setPlaybackState(true, { sendCommand: false, updateIntent: false })
        }
        return
      }

      if (message.playerState === YOUTUBE_PLAYER_STATE.PAUSED) {
        setPlaybackState(false, { sendCommand: false, updateIntent: false })
        return
      }

      if (message.playerState === YOUTUBE_PLAYER_STATE.ENDED && !loopSlideRef.current) {
        setPlaybackState(false, { sendCommand: false, updateIntent: false })
        setPlayedSeconds(videoEnd)
      }
    }

    window.addEventListener('message', handleYoutubeMessage)

    return () => {
      window.removeEventListener('message', handleYoutubeMessage)
    }
  }, [setPlaybackState, videoEnd, videoStart])

  useEffect(() => {
    if (!isPlaying || !isPlayerLoaded) {
      clearProgressTimer()
      return undefined
    }

    clearProgressTimer()
    progressTimerRef.current = window.setInterval(() => {
      if (!isPlayingRef.current) {
        clearProgressTimer()
        return
      }

      setPlayedSeconds((currentSeconds) => {
        const nextSeconds = currentSeconds + (PLAYER_PROGRESS_TICK_MS / 1000) * playbackRateRef.current
        const transition = legacyShortVideoContract.getLegacyShortVideoPlaybackTransition({
          content: segments,
          end: videoEnd,
          loopNumberLooped: loopNumberLoopedRef.current,
          loopSlide: loopSlideRef.current,
          mainSegmentEndGraceSeconds:
            legacyShortVideoContract.LEGACY_SHORT_VIDEO_MAIN_REPEAT_END_GRACE_SECONDS,
          playedSeconds: nextSeconds,
          slideIndex: slideIndexRef.current,
          start: videoStart,
        })

        if (transition.action === 'seek-active-slide' || transition.action === 'seek-video-start') {
          sendPlayerCommand('seekTo', [transition.seekTo, true])
          setSlideIndex(transition.slideIndex)
          setLoopNumberLooped(transition.loopNumberLooped)
          return transition.seekTo ?? nextSeconds
        }

        if (transition.action === 'select-slide') {
          setSlideIndex(transition.slideIndex)
        }

        return nextSeconds
      })
    }, PLAYER_PROGRESS_TICK_MS)

    return clearProgressTimer
  }, [clearProgressTimer, isPlayerLoaded, isPlaying, segments, sendPlayerCommand, videoEnd, videoStart])

  useEffect(
    () => () => {
      clearAutoplayTimeout()
      clearProgressTimer()
    },
    [clearAutoplayTimeout, clearProgressTimer],
  )

  return (
    <section className="shortVideoLearningPlayer" data-qa="short-video-learning-player">
      <div className="shortVideoPlayerHeader">
        <div className="shortVideoPlayerTopBar">
          <div className="shortVideoPlayerBack">
            <LmsButton
              aria-label={t('Back')}
              onClick={onReturn}
              size="icon"
              variant="ghost"
            >
              <ArrowLeft aria-hidden="true" />
            </LmsButton>
            <span className="shortVideoPlayerSlideCount">
              {slideIndex + 1} / {segments.length}
            </span>
          </div>
          <MediaControlBar
            aria-label={t('Video actions')}
            className="shortVideoPlayerActions"
            kind="video"
            layout="legacy-header"
          >
            <div className="shortVideoPlayerSpeed">
              <span>{t('Speed')}</span>
              <SelectMenu
                onValueChange={(value) => {
                  setPlaybackRate(Number(value))
                }}
                value={String(playbackRate)}
              >
                <SelectMenuTrigger aria-label={t('Speed')} className="shortVideoPlayerSpeedSelect">
                  <SelectMenuValue />
                </SelectMenuTrigger>
                <SelectMenuContent align="end">
                {videoSpeeds.map((speed) => (
                  <SelectMenuItem key={speed.value} value={String(speed.value)}>
                    {t(speed.label)}
                  </SelectMenuItem>
                ))}
                </SelectMenuContent>
              </SelectMenu>
            </div>
            <LmsButton
              aria-label={t('Loop')}
              aria-pressed={loopSlide}
              className={`lmsMediaControlButton shortVideoPlayerLoopButton${loopSlide ? ' active' : ''}`}
              onClick={() => {
                setLoopSlide((value) => !value)
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Repeat2 aria-hidden="true" />
            </LmsButton>
          </MediaControlBar>
        </div>
        <MediaControlBar
          aria-label={t('Video progress')}
          className="shortVideoPlayerProgress"
          data-qa="short-video-media-control-bar"
          kind="video"
          layout="legacy-progress"
        >
          <input
            aria-label={t('Video progress')}
            className="lmsMediaControlProgress"
            disabled={!embedUrl}
            max={100}
            min={0}
            onChange={(event) => {
              handleSeekPercent(Number(event.currentTarget.value))
            }}
            step={1}
            style={{ '--lms-media-control-progress': `${progress}%` } as CSSProperties}
            type="range"
            value={progress}
          />
        </MediaControlBar>
      </div>

      <div className="shortVideoPlayerViewport">
        {embedUrl ? (
          <iframe
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="shortVideoPlayerFrame"
            onLoad={handlePlayerLoad}
            ref={iframeRef}
            src={embedUrl}
            title={video.title ?? 'Short video'}
          />
        ) : (
          <div className="shortVideoPlayerFrame shortVideoPlayerEmpty">
            {thumbnail ? <img alt="" src={thumbnail} /> : <Play aria-hidden="true" />}
          </div>
        )}
        {!isPlaying && thumbnail ? (
          <div aria-hidden="true" className="shortVideoPlayerPoster">
            <img alt="" src={thumbnail} />
          </div>
        ) : null}
        {embedUrl ? (
          <LmsButton
            aria-label={isPlaying ? t('Pause') : t('Play')}
            className={`shortVideoPlayerOverlay ${isPlaying ? 'isPlaying' : ''}`}
            onClick={handleTogglePlay}
            size="icon-lg"
            variant="ghost"
          >
            {!isPlaying ? <Play aria-hidden="true" /> : null}
          </LmsButton>
        ) : null}
        {isPlaying ? (
          <LmsButton
            className="shortVideoTranscriptModeButton"
            onClick={handleCycleTranscript}
            size="sm"
            variant="outline"
          >
            {getTranscriptModeLabel({ locale, mode: transcriptMode })}
          </LmsButton>
        ) : null}
      </div>

      {(segmentText || segmentTranslation) ? (
        <div className="shortVideoSegmentSubtitles">
          <div className="shortVideoTransport">
            <LmsButton
              aria-label={t('Back slide')}
              disabled={loopSlide}
              onClick={() => {
                handleNavigateToSlide(slideIndex > 0 ? slideIndex - 1 : segments.length - 1)
              }}
              size="icon"
              variant="ghost"
            >
              <ArrowLeft aria-hidden="true" />
            </LmsButton>
            <LmsButton
              aria-label={isPlaying ? t('Pause') : t('Play')}
              className="shortVideoTransportPlayButton"
              onClick={handleTogglePlay}
              size="icon-lg"
              variant={isPlaying ? 'secondary' : 'primary'}
            >
              {isPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
            </LmsButton>
            <LmsButton
              aria-label={t('Next slide')}
              disabled={loopSlide}
              onClick={() => {
                handleNavigateToSlide(slideIndex < segments.length - 1 ? slideIndex + 1 : 0)
              }}
              size="icon"
              variant="ghost"
            >
              <ArrowRight aria-hidden="true" />
            </LmsButton>
          </div>
          <div className="shortVideoSegmentText">
            {segmentText && transcriptMode !== 'target' ? (
              <SafeHtml as="h2" html={segmentText} />
            ) : null}
            {segmentTranslation && transcriptMode !== 'vi' ? (
              <SafeHtml as="p" html={segmentTranslation} />
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
})
